import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  QUERY_STATUS,
  DOCUMENTS_BASE_URL_BY_ENTITY,
  SCOPES,
  ROLE_MATCH_MODE,
  DOCX_MIME_TYPE,
  PDF_MIME_TYPE,
} from "constants/constants";
import { useUserContext } from "api/user/UserProvider";
import { downloadFile } from "utils/helper";
import {
  getAvailableDocumentsQuery,
  getDocumentBlob,
  getSixYearReviewBlob,
} from "./queries";
import { documentGetterInfos } from "utils/documentsHelper";
import { canUserPerformAction } from "access-roles/general-functions";
import { MISSION_ROLES } from "access-roles/roles";

const DocumentsContext = createContext({});

export const useDocumentsContext = () => useContext(DocumentsContext);

export const DocumentsProvider = ({ ...props }) => {
  const { userScope, userRoleList } = useUserContext();

  const [availableDocuments, setAvailableDocuments] = useState([]);

  const [getAvailableDocumentsStatus, setGetAvailableDocumentsStatus] =
    useState(QUERY_STATUS.INITIAL);

  const [getDocumentStatus, setGetDocumentStatus] = useState(
    QUERY_STATUS.INITIAL
  );

  const getAvailableDocuments = useCallback(
    async ({ entityId, BASE_URL }) => {
      if (BASE_URL === DOCUMENTS_BASE_URL_BY_ENTITY.MISSION) {
        const requiredRolesByScope = {
          [SCOPES.PERSO]: {
            requiredRoles: MISSION_ROLES.ROLE_UPDATE_MISSION,
          },
          [SCOPES.MACRO]: {
            requiredRoles: [
              MISSION_ROLES.ROLE_UPDATE_MISSION,
              MISSION_ROLES.ROLE_MACRO_AS_ADMIN_MISSION,
            ],
            mode: ROLE_MATCH_MODE.OR,
          },
          [SCOPES.ADMIN]: {
            requiredRoles: MISSION_ROLES.ROLE_ADMIN_MISSION,
          },
        };

        const canAccess = canUserPerformAction({
          userRoles: userRoleList,
          ...requiredRolesByScope[userScope],
        });

        if (!canAccess) {
          return;
        }
      }

      setGetAvailableDocumentsStatus(QUERY_STATUS.LOADING);
      try {
        const result = await getAvailableDocumentsQuery({
          entityId,
          userScope,
          BASE_URL,
        });
        setAvailableDocuments(result);
        setGetAvailableDocumentsStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        console.error("getAvailableDocuments", e);
        setGetAvailableDocumentsStatus(QUERY_STATUS.ERROR);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const downloadFileQuery = async ({ documentTag, queryParams, BASE_URL }) => {
    const fileBlob = await getDocumentBlob({
      type: documentGetterInfos[documentTag]?.type,
      queryParams,
      BASE_URL,
    });
    return fileBlob;
  };

  const getDocument = useCallback(
    async ({ queryParams, documentTag, BASE_URL }) => {
      try {
        setGetDocumentStatus(QUERY_STATUS.LOADING);
        const isPdf = queryParams?.["doc-type"] === "pdf";
        const fileType = isPdf ? PDF_MIME_TYPE : DOCX_MIME_TYPE;
        const fileName =
          documentGetterInfos?.[documentTag]?.[
            isPdf ? "fileNamePDF" : "fileName"
          ];
        await downloadFile({
          fileType,
          fileName,
          downloadFileQuery,
          userScope,
          documentTag,
          queryParams,
          BASE_URL,
        });
        setGetDocumentStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setGetDocumentStatus(QUERY_STATUS.ERROR);
        console.error(
          "Erreur lors de la génération ou du téléchargement du fichier:",
          error
        );
      }
    },
    [userScope]
  );

  const downloadReviewFile = useCallback(
    async ({ id, fileName, fileType = DOCX_MIME_TYPE }) => {
      setGetDocumentStatus(QUERY_STATUS.LOADING);
      try {
        await downloadFile({
          fileType,
          fileName,
          downloadFileQuery: getSixYearReviewBlob,
          file_id: id,
        });
        setGetDocumentStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setGetDocumentStatus(QUERY_STATUS.ERROR);
        console.error("Erreur lors du téléchargement du fichier:", error);
      }
    },
    []
  );

  const values = useMemo(
    () => ({
      userScope,
      getAvailableDocuments,
      getAvailableDocumentsStatus,
      setGetAvailableDocumentsStatus,
      availableDocuments,
      setAvailableDocuments,
      getDocument,
      getDocumentStatus,
      setGetDocumentStatus,
      downloadReviewFile,
    }),
    [
      userScope,
      getAvailableDocuments,
      getAvailableDocumentsStatus,
      setGetAvailableDocumentsStatus,
      availableDocuments,
      setAvailableDocuments,
      getDocument,
      getDocumentStatus,
      setGetDocumentStatus,
      downloadReviewFile,
    ]
  );
  return <DocumentsContext.Provider value={values} {...props} />;
};
